import React, { useEffect, useState, useRef, useCallback } from 'react';
import axiosInstance from '../api/axiosInstance';
import { isVideoFile } from '../utils/mediaUtils';
import { CircularProgress } from '@mui/material';

function MyTrippinGrid({ fetchUrl = '/mytrippins', isOwnerTab = false }) {
  const [trippins, setTrippins] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const observer = useRef();
  const LIMIT = 20;
  console.log(trippins);
  const loadTrippins = async (pageNum) => {
    try {
      setLoading(true);
      const res = await axiosInstance.get(`${fetchUrl}?page=${pageNum}&limit=${LIMIT}`);
      // always append new rows; stop when we receive a short page
      setTrippins(prev => [
        ...prev,
        ...res.data.filter(row => !prev.some(p => p.trippin_id === row.trippin_id)),
      ]);
      setHasMore(res.data.length === LIMIT);
      setPage(pageNum);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setTrippins([]);
    setPage(1);
    setHasMore(true);
    loadTrippins(1);
  }, [fetchUrl]);

  const lastTrippinRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadTrippins(page + 1);
        }
      });

      if (node) observer.current.observe(node);
    },
    [loading, hasMore, page]
  );

  if (!trippins.length && loading) {
    return (
      <div className="loading-container">
        <CircularProgress size={40} sx={{ color: 'black' }} />
      </div>
    );
  }

  if (!trippins.length && !loading) {
    return (
      <div className="loading-container" style={{ height: 'unset' }}>
        <p style={{ fontSize: '18px', color: '#d1d5db', textAlign: 'center', marginTop: '50px' }}>
          You have no trippins here yet
        </p>
      </div>
    );
  }

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '3px', marginTop: '20px' }}>
      {trippins.map((tr, i) => {
        const isVideo = isVideoFile(tr.firstImageUrl);
        const isLast = i === trippins.length - 1;

        return (
          <div
            key={tr.trippin_id}
            ref={isLast ? lastTrippinRef : null}
            onClick={async () => {
              const domNode = document.getElementById('trippin-root');
              if (!domNode) return;

              const { mount, unmount } = await import('./TrippinPost/index.js');
              const source = isOwnerTab ? 'mytrippins' : 'taggedtrippins';
              const trippin_ids = trippins.map(t => t.trippin_id);
              const selectedIndex = trippins.findIndex(t => t.trippin_id === tr.trippin_id);
              mount(domNode, {
                trippin_ids,
                selectedTrippinId: tr.trippin_id,
                source,
                offset: selectedIndex,
                onExit: ({ exitTrippinId }) => {
                  unmount();
                },
              });
            }}
            style={{
              position: 'relative',
              width: '100%',
              paddingBottom: 'calc(100% * 4 / 3)',
              overflow: 'hidden',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {isVideo ? (
              <video
                src={tr.firstImageUrl}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                }}
                autoPlay
                loop
                muted
                playsInline
              />
            ) : (
              <img
                src={tr.firstImageUrl || '/defaultPlaceholder.jpg'}
                style={{
                  position: 'absolute',
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  top: 0,
                  left: 0,
                }}
                alt="Trippin Thumbnail"
              />
            )}

            <div
              style={{
                position: 'absolute',
                bottom: '8px',
                width: '100%',
                textAlign: 'center',
                color: 'white',
                fontWeight: 'bold',
              }}
            >
              {new Intl.DateTimeFormat('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              }).format(new Date(tr.start_time))}
            </div>
          </div>
        );
      })}
      {loading && (
        <div style={{ gridColumn: '1 / -1', textAlign: 'center', margin: '20px 0' }}>
          <CircularProgress size={32} sx={{ color: 'black' }} />
        </div>
      )}
    </div>
  );
}

export default MyTrippinGrid;
